import moment from "moment";
import React, { FunctionComponent } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { IGCPBigQueryJob } from "../../../../../Models/IGCPBigQueryJob";
import { gpsInsightForReportView } from "../../../../../Reducers/Selectors/GPSInsightsSelectors";
import {
    BodyText,
    ReportDateBlockHeaderText,
} from "../../../../Common/ReportStyles/Text";
import { VscCalendar } from "@react-icons/all-files/vsc/VscCalendar";
import { AiOutlineClockCircle } from "@react-icons/all-files/ai/AiOutlineClockCircle";
import { BsGraphUp } from "@react-icons/all-files/bs/BsGraphUp";
import { BsArrowUpShort } from "@react-icons/all-files/bs/BsArrowUpShort";
import { BsArrowDownShort } from "@react-icons/all-files/bs/BsArrowDownShort";
import { BiStoreAlt } from "@react-icons/all-files/bi/BiStoreAlt";
import { FaMapMarkerAlt } from "@react-icons/all-files/fa/FaMapMarkerAlt";
import { digitalShareElementText } from "../../../../Common/DigitalShare/DigitalShareGenerator";
import {
    PolygonComparison,
    PolygonComparisonDates,
    Sidebar,
    SidebarMoreInfo,
    SidebarSupp,
    SidebarTitle,
    SupText,
} from "./GPSTemplateStyles";
import {
    MainContent,
    MainContentChild,
    MainContentText,
    MainContentTitle,
    Wrapper,
} from "../../../../Common/DigitalShare/Templates/TemplateStyles";
import DigitalShareTemplate from "../../../../Common/DigitalShare/Templates/DigitalShareTemplate";
import { DigitalShareAppType } from "../../../../../Enums/DigitalShareAppType";

interface IDigitalSharePolygonComparison {
  polygonName: string;
  fromDateTime: Date;
  toDateTime: Date;
}
export interface IGPSInsightsDigitalShareTemplate {
  children: React.ReactNode;
  polygonComparisons: IDigitalSharePolygonComparison[];
  captionAboveTitle?: string;
  sectionTitle: string;
  disclaimer: string;
  itemType: string;
  hideItemTypeTitle?: boolean;
  hideItemText?: boolean;
}

const GPSInsightsDigitalShareTemplate: FunctionComponent<IGPSInsightsDigitalShareTemplate>
  = (props: IGPSInsightsDigitalShareTemplate) => {
      const data: IGCPBigQueryJob[] = useSelector(gpsInsightForReportView);

      const handleFormatReportDate = (date: Date): string => {
          return moment(date).format("MM/DD/YYYY");
      };

      const handleFormatReportTime = (date: Date): string => {
          return moment(date).format("hh:mm A");
      };

      const handlePolygonLessOrGreater = (
          sign: string,
          miles: number
      ): string => {
          return `Includes Households ${
              sign == "GREATER" ? "Greater Than" : "Less Than"
          } ${miles} miles.`;
      };

      return (
          <DigitalShareTemplate
              appName={DigitalShareAppType.Mobilytics}
              captionAboveTitle={props.captionAboveTitle}
              sectionTitle={props.sectionTitle}
          >
              <Wrapper data-testid="gps-share-template">
                  <Sidebar>
                      {data.map((d, i, arr) => (
                          <div key={`${i}-${d.jobPolygon.name}-sidebar`}>
                              <PolygonComparison
                                  key={`${i}-${d.jobPolygon.name}-comparison`}
                                  data-testid="gps-template-polygon-comparison"
                              >
                                  <div>
                                      <BodyText
                                          style={{
                                              fontWeight: "bold",
                                              marginBottom: 0,
                                              textAlign: "center",
                                              width: "278px",
                                          }}
                                      >
                                          {d.jobPolygon.name}
                                      </BodyText>
                                  </div>
                                  {d.miles > 0 && (
                                      <div data-testid="gps-template-polygon-comparison-mileage">
                                          {handlePolygonLessOrGreater(
                                              d.milesLessOrGreater,
                                              d.miles
                                          )}
                                      </div>
                                  )}
                                  <div
                                      style={{
                                          width: "164px",
                                          height: "1px",
                                          margin: "2px 74px 4px 73px",
                                          border: "solid 1px #e4e4e4",
                                      }}
                                  />
                                  <PolygonComparisonDates>
                                      <ReportDateBlockHeaderText
                                          style={{
                                              display: "flex",
                                              fontWeight: "normal",
                                              margin: "0px",
                                              fontSize: "13px",
                                          }}
                                      >
                                          <VscCalendar style={{ marginRight: "5px" }} />{" "}
                                          {handleFormatReportDate(d.queryFromDateTime)} -{" "}
                                          {handleFormatReportDate(d.queryToDateTime)}
                                      </ReportDateBlockHeaderText>
                                      <ReportDateBlockHeaderText
                                          style={{
                                              display: "flex",
                                              fontWeight: "normal",
                                              margin: "0px",
                                              fontSize: "13px",
                                          }}
                                      >
                                          <AiOutlineClockCircle style={{ marginRight: "5px" }} />{" "}
                                          {handleFormatReportTime(d.queryFromDateTime)} -{" "}
                                          {handleFormatReportTime(d.queryToDateTime)}
                                      </ReportDateBlockHeaderText>
                                  </PolygonComparisonDates>
                              </PolygonComparison>

                              {i < 1 && arr.length > 1 && (
                                  <div
                                      style={{
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          color: "#2b8b49",
                                          margin: "13px 0px 13px",
                                          fontWeight: 600,
                                      }}
                                  >
                                      <BsArrowUpShort size={20} /> COMPARE TO{" "}
                                      <BsArrowDownShort size={20} />
                                  </div>
                              )}
                          </div>
                      ))}
                      <SidebarSupp style={{ marginTop: "8px" }}>
                          <SidebarTitle>
                The most detailed view of foot-traffic possible.
                          </SidebarTitle>

                          <BodyText
                              style={{
                                  fontSize: "13px",
                                  fontWeight: "normal",
                                  textAlign: "center",
                              }}
                          >
                Buxton’s Mobilytics allows you to create, analyze, and visualize
                mobile data sets for any place and any available time period.
                          </BodyText>
                          <SupText>
                              <FaMapMarkerAlt size="24" style={{ marginRight: "10px" }} />
                Understand who visited your site and where they came from.
                          </SupText>
                          <SupText>
                              <BsGraphUp size="24" style={{ marginRight: "10px" }} />
                Benchmark visitor volume trends by date and time.
                          </SupText>
                          <SupText>
                              <VscCalendar size="24" style={{ marginRight: "10px" }} />
                Reports based on data that’s updated daily.
                          </SupText>
                          <SupText>
                              <BiStoreAlt size="24" style={{ marginRight: "10px" }} />
                Compare competitors foot traffic.
                          </SupText>

                          <SidebarMoreInfo>
                Visit{" "}
                              <a href="https://buxtonco.com/mobilytics">
                  buxtonco.com/mobilytics
                              </a>{" "}
                for more information
                          </SidebarMoreInfo>
                      </SidebarSupp>
                  </Sidebar>
                  <MainContent>
                      {!props.hideItemTypeTitle && (
                          <MainContentTitle>{props.itemType}</MainContentTitle>
                      )}
                      {!props.hideItemText && (
                          <MainContentText>
                              {digitalShareElementText.get(props.itemType)}
                          </MainContentText>
                      )}
                      <MainContentChild>{props.children}</MainContentChild>
                  </MainContent>
              </Wrapper>
          </DigitalShareTemplate>
      );
  };

export default GPSInsightsDigitalShareTemplate;
